import { shuffleTurnOrder } from '@utils/turnManager';

/**
 * Zone presets for different zone counts.
 * Each zone has a name, emoji, and colour theme used in the UI.
 */
const ZONE_PRESETS = {
  3: [
    { name: 'Head', emoji: '🧠', colour: 'rose' },
    { name: 'Body', emoji: '🫁', colour: 'sky' },
    { name: 'Legs', emoji: '🦵', colour: 'emerald' },
  ],
  4: [
    { name: 'Head', emoji: '🧠', colour: 'rose' },
    { name: 'Torso', emoji: '🫁', colour: 'sky' },
    { name: 'Hips', emoji: '🦴', colour: 'violet' },
    { name: 'Legs', emoji: '🦵', colour: 'emerald' },
  ],
  5: [
    { name: 'Head', emoji: '🧠', colour: 'rose' },
    { name: 'Shoulders', emoji: '💪', colour: 'amber' },
    { name: 'Torso', emoji: '🫁', colour: 'sky' },
    { name: 'Hips', emoji: '🦴', colour: 'violet' },
    { name: 'Legs', emoji: '🦵', colour: 'emerald' },
  ],
};

/**
 * Get zone metadata for a given zone count.
 * @param {number} zoneCount - 3, 4, or 5
 * @returns {Array<{name: string, emoji: string, colour: string}>}
 */
export function getZonePreset(zoneCount) {
  return ZONE_PRESETS[zoneCount] || ZONE_PRESETS[3];
}

/**
 * Get just the zone names for a given zone count.
 * @param {number} zoneCount - 3, 4, or 5
 */
export function getZoneNames(zoneCount) {
  return getZonePreset(zoneCount).map((z) => z.name);
}

// Default 3-zone names for backwards compatibility
export const ZONE_NAMES = ZONE_PRESETS[3].map((z) => z.name);

/**
 * Tailwind colour classes per colour key, used for zone labels and diagrams.
 */
export const ZONE_COLOUR_CLASSES = {
  rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-300', highlight: 'bg-rose-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-300', highlight: 'bg-amber-500' },
  sky: { bg: 'bg-sky-100', text: 'text-sky-600', border: 'border-sky-300', highlight: 'bg-sky-500' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-300', highlight: 'bg-violet-500' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-300', highlight: 'bg-emerald-500' },
};

/**
 * Canvas height is 1500px. Zones divide it equally.
 * @param {number} zoneCount
 * @returns {number} pixel height of each zone
 */
export function getZoneHeight(zoneCount) {
  return 1500 / (zoneCount || 3);
}

/**
 * Get the Y-coordinate boundaries for zone boundary lines.
 * Returns an array of Y values where dashed lines should be drawn.
 * @param {number} zoneCount
 * @returns {number[]}
 */
export function getZoneBoundaries(zoneCount) {
  const h = getZoneHeight(zoneCount);
  const boundaries = [];
  for (let i = 1; i < zoneCount; i++) {
    boundaries.push(h * i);
  }
  return boundaries;
}

/**
 * Assign players to creature zones for Exquisite Corpse.
 *
 * @param {string[]} memberIds - Array of user IDs
 * @param {Object} members - Room members map { [userId]: { nickname, colour, ... } }
 * @param {number} zoneCount - 3, 4, or 5 zones per creature
 * @returns {{ creatures: Object[], totalCreatures: number }}
 */
export function assignZones(memberIds, members, zoneCount = 3) {
  const shuffled = shuffleTurnOrder(memberIds);
  const totalCreatures = Math.max(1, Math.ceil(shuffled.length / zoneCount));
  const totalSlots = totalCreatures * zoneCount;

  const creatures = {};

  for (let c = 0; c < totalCreatures; c++) {
    creatures[c] = {
      zones: {},
      fullSnapshot: null,
    };
  }

  // Fill all slots by cycling through shuffled players
  for (let slot = 0; slot < totalSlots; slot++) {
    const playerId = shuffled[slot % shuffled.length];
    const creatureIndex = Math.floor(slot / zoneCount);
    const zoneIndex = slot % zoneCount;

    creatures[creatureIndex].zones[zoneIndex] = {
      assignedTo: playerId,
      nickname: members[playerId]?.nickname || 'Unknown',
      snapshot: null,
    };
  }

  return { creatures, totalCreatures };
}
